import { useRef, useState, useCallback } from 'react';
import { getAuthHeaders, API_BASE_URL } from '../auth';
import { encodeWAV } from '../utils/audioUtils';

/**
 * Reusable one-shot speech-to-text hook.
 *
 * Records microphone audio via a ScriptProcessor, encodes it to WAV using the
 * actual capture sample rate (so the WAV header matches the data — see
 * encodeWAV / audioUtils), sends it to /api/translate_audio_to_text, and hands
 * the transcript back through onResult.
 *
 * Supports multiple independent targets ("fields") sharing one recorder: only
 * one field can record at a time. `toggle(field)` starts recording for that
 * field, or stops-and-transcribes if it is already the active field.
 *
 * @param {(field: string, text: string) => void} onResult  called with the transcript
 * @param {(field: string, error: Error) => void} [onError] called on failure
 */
export default function useSpeechToText({ onResult, onError } = {}) {
  const [recordingField, setRecordingField] = useState(null);
  const [transcribingField, setTranscribingField] = useState(null);

  const audioContextRef = useRef(null);
  const mediaStreamSourceRef = useRef(null);
  const processorRef = useRef(null);
  const audioBufferRef = useRef([]);

  const teardown = useCallback(() => {
    if (processorRef.current && mediaStreamSourceRef.current) {
      mediaStreamSourceRef.current.disconnect();
      processorRef.current.disconnect();
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
    if (mediaStreamSourceRef.current && mediaStreamSourceRef.current.mediaStream) {
      mediaStreamSourceRef.current.mediaStream.getTracks().forEach(track => track.stop());
    }
    processorRef.current = null;
    mediaStreamSourceRef.current = null;
    audioContextRef.current = null;
  }, []);

  const transcribe = useCallback((buffer, sampleRate, field) => {
    setTranscribingField(field);
    const wavBlob = encodeWAV(buffer, sampleRate);
    const reader = new FileReader();
    reader.readAsDataURL(wavBlob);
    reader.onloadend = async () => {
      const base64data = reader.result.split(',')[1];
      try {
        const response = await fetch(`${API_BASE_URL}/api/translate_audio_to_text`, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ audio_file: base64data }),
        });
        if (!response.ok) throw new Error('Failed to process audio');
        const data = await response.json();
        if (data && data.user_input && onResult) {
          onResult(field, data.user_input);
        }
      } catch (error) {
        console.error('Error processing audio:', error);
        if (onError) onError(field, error);
      } finally {
        setTranscribingField(null);
      }
    };
  }, [onResult, onError]);

  const toggle = useCallback(async (field) => {
    // Stop the currently recording field and transcribe it.
    if (recordingField === field) {
      // Capture the actual capture rate before closing the context; the WAV
      // header must match the native rate the ScriptProcessor recorded at.
      const sampleRate = audioContextRef.current ? audioContextRef.current.sampleRate : 44100;
      const buffer = audioBufferRef.current;
      teardown();
      setRecordingField(null);
      transcribe(buffer, sampleRate, field);
      return;
    }

    // Another field is mid-recording — ignore until it is stopped.
    if (recordingField) return;

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Your browser does not support audio recording.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      mediaStreamSourceRef.current = audioContext.createMediaStreamSource(stream);
      processorRef.current = audioContext.createScriptProcessor(4096, 1, 1);

      mediaStreamSourceRef.current.connect(processorRef.current);
      processorRef.current.connect(audioContext.destination);

      audioBufferRef.current = [];
      processorRef.current.onaudioprocess = (e) => {
        audioBufferRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };

      setRecordingField(field);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Could not access your microphone. Please check permissions.');
    }
  }, [recordingField, teardown, transcribe]);

  return { recordingField, transcribingField, toggle };
}
